import type { ReactNode } from 'react';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import type { VersionedSettings } from '../../model/settingsTypes';

export interface SettingsScaffoldProps<T> {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Primary settings query for this domain. */
  query: UseQueryResult<T, NormalizedApiError>;
  /** Whether the current employee may view this domain. */
  canView: boolean;
  /** Whether the current employee may edit/save this domain. */
  canManage: boolean;
  /** Loaded resource audit info, used for the version/updated header chip. */
  audit?: VersionedSettings | null;
  /** Save-bar wiring (omit on read-only or action-only screens). */
  dirty?: boolean;
  saving?: boolean;
  onSave?: () => void;
  onReset?: () => void;
  /** Label used in the save-bar primary action. */
  saveLabel?: string;
  /** Renders with the loaded data. */
  children: (data: T) => ReactNode;
  /** Optional feedback element (e.g. a `<FeedbackSnackbar />`). */
  feedback?: ReactNode;
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
}

/**
 * Shared shell for every settings screen.
 *
 * Owns the cross-cutting concerns so each domain page only describes its fields:
 * a back link, version/updated header chips, the permission gate (no-access /
 * read-only), the load / error / content states, and a sticky save bar.
 */
export function SettingsScaffold<T>({
  title,
  subtitle,
  icon,
  query,
  canView,
  canManage,
  audit,
  dirty = false,
  saving = false,
  onSave,
  onReset,
  saveLabel = 'Save changes',
  children,
  feedback,
}: SettingsScaffoldProps<T>) {
  const updatedAt = formatTimestamp(audit?.updatedAt);
  const showSaveBar = canView && canManage && Boolean(onSave) && query.isSuccess;

  let body: ReactNode;
  if (!canView) {
    body = (
      <ErrorState
        title="Access restricted"
        message="You don't have permission to view these settings. Contact a tenant administrator if you need access."
      />
    );
  } else if (query.isLoading) {
    body = <LoadingState label="Loading settings…" />;
  } else if (query.isError) {
    // The BFF returns HTTP 503 / code "owner-unavailable" when the owning
    // service is physically down (circuit-breaker opened or bounded timeout
    // exceeded). This is a contained, per-domain failure — the rest of the
    // settings hub and nav must remain fully functional.
    const isOwnerUnavailable =
      query.error?.status === 503 || query.error?.code === 'owner-unavailable';

    body = (
      <ErrorState
        title={isOwnerUnavailable ? 'Service temporarily unavailable' : "Couldn't load settings"}
        message={
          isOwnerUnavailable
            ? 'This settings section cannot be loaded right now. Other settings are not affected. Try again in a moment.'
            : (query.error?.message ?? 'An unexpected error occurred.')
        }
        onRetry={() => void query.refetch()}
      />
    );
  } else if (query.isSuccess) {
    body = (
      <Stack spacing={2.5}>
        {!canManage ? (
          <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
            You have read-only access to these settings.
          </Alert>
        ) : null}
        {children(query.data)}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        maxWidth: 1100,
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Button
        component={RouterLink}
        to="/settings"
        size="small"
        color="inherit"
        startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
        sx={{ mb: 2, ml: -1, color: 'text.secondary' }}
      >
        All settings
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {icon ? (
            <Box
              aria-hidden
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 44,
                height: 44,
                borderRadius: 2.5,
                flexShrink: 0,
                color: '#fff',
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {audit ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`Version ${audit.version}`} />
            {updatedAt ? (
              <Chip size="small" variant="outlined" label={`Updated ${updatedAt}`} />
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <Box sx={{ mt: 3 }}>{body}</Box>

      {showSaveBar ? (
        <Paper
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 3,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 2,
            boxShadow: '0 8px 28px rgba(16, 24, 40, 0.12)',
          }}
        >
          <Typography
            variant="body2"
            color={dirty ? 'text.primary' : 'text.secondary'}
            sx={{ flexGrow: 1, pl: 1 }}
          >
            {dirty ? 'You have unsaved changes' : 'All changes saved'}
          </Typography>
          <Button onClick={onReset} disabled={!dirty || saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!dirty || saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />
            }
          >
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </Paper>
      ) : null}

      {feedback}
    </Box>
  );
}
